import * as yup from 'yup';
import {
  TOrderCheckoutItemProductType,
  TOrderStatusEnum,
} from 'api/orders/orders.types';
import {
  MAX_DEFAULT_BIG_FIELD_SYMBOLS,
  MAX_DEFAULT_FIELD_SYMBOLS,
} from 'constants/general';
import type { TLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.types';
import { TOrderFormValues } from './OrderForm.types';

const shippingDefaultMaxMessage = (localize: TLocalize, messageKey: string) =>
  localize(messageKey, [String(MAX_DEFAULT_FIELD_SYMBOLS)]) as string;

export const getOrderFormSchema = (localize: TLocalize) => {
  const stockLineSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.stock.name.required') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.stock.name.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    variation: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.stock.variation.required') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.stock.variation.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
  });

  const checkoutItemSchema = yup.object().shape({
    price: yup
      .number()
      .typeError(localize('validation.admin.orders.price.number') as string)
      .min(0, localize('validation.admin.orders.price.min') as string)
      .required(localize('validation.admin.orders.price.required') as string),
    productId: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.product.id.required') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.product.id.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    productType: yup
      .mixed<TOrderCheckoutItemProductType>()
      .oneOf(
        [TOrderCheckoutItemProductType.PhoneCase],
        localize('validation.admin.orders.product.type.invalid') as string
      )
      .required(),
    productName: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.product.name.required') as string
      )
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.product.name.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    productImage: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.product.image.required') as string
      )
      .max(
        MAX_DEFAULT_BIG_FIELD_SYMBOLS,
        localize('validation.admin.orders.product.image.max', [
          String(MAX_DEFAULT_BIG_FIELD_SYMBOLS),
        ]) as string
      ),
    quantity: yup
      .number()
      .typeError(localize('validation.admin.orders.quantity.number') as string)
      .integer(localize('validation.admin.orders.quantity.integer') as string)
      .min(1, localize('validation.admin.orders.quantity.min') as string)
      .required(
        localize('validation.admin.orders.quantity.required') as string
      ),
    stock: yup
      .array()
      .of(stockLineSchema)
      .min(1, localize('validation.admin.orders.stock.min') as string)
      .required(),
  });

  return yup.object().shape({
    id: yup
      .string()
      .trim()
      .required(localize('validation.admin.orders.id.required') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.id.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    orderNumber: yup
      .string()
      .trim()
      .required(localize('validation.admin.orders.number.required') as string)
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.number.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    totalPrice: yup
      .number()
      .typeError(localize('validation.admin.orders.total.number') as string)
      .min(0, localize('validation.admin.orders.total.min') as string)
      .required(localize('validation.admin.orders.total.required') as string),
    status: yup
      .mixed<TOrderStatusEnum>()
      .oneOf(
        Object.values(TOrderStatusEnum),
        localize('validation.admin.orders.status.invalid') as string
      )
      .required(),
    payed: yup.boolean().required(),
    receiveNotifications: yup.boolean().required(),
    policyAccepted: yup.boolean().required(),
    isManualPayment: yup.boolean().required(),
    manualPaymentDate: yup.string().when('isManualPayment', {
      is: true,
      then: (schema) =>
        schema
          .trim()
          .required(
            localize('validation.admin.orders.datetime.required') as string
          ),
      otherwise: (schema) => schema.optional(),
    }),
    manualPaymentMethod: yup
      .mixed<'cash' | 'bankTransfer' | ''>()
      .when('isManualPayment', {
        is: true,
        then: (schema) =>
          schema.oneOf(
            ['cash', 'bankTransfer'],
            localize('validation.admin.orders.status.invalid') as string
          ),
        otherwise: (schema) => schema.oneOf(['']),
      }),
    paymentJson: yup.string().when('isManualPayment', {
      is: false,
      then: (schema) =>
        schema
          .required()
          .max(
            MAX_DEFAULT_BIG_FIELD_SYMBOLS,
            localize('validation.admin.orders.payment.max', [
              String(MAX_DEFAULT_BIG_FIELD_SYMBOLS),
            ]) as string
          )
          .test(
            'json',
            localize('validation.admin.orders.payment.json') as string,
            (value) => {
              if (value === undefined || value === null) {
                return false;
              }
              const trimmed = String(value).trim();
              if (!trimmed) {
                return false;
              }
              try {
                JSON.parse(trimmed);
                return true;
              } catch {
                return false;
              }
            }
          ),
      otherwise: (schema) => schema.optional(),
    }),
    userId: yup
      .string()
      .trim()
      .max(
        MAX_DEFAULT_FIELD_SYMBOLS,
        localize('validation.admin.orders.userid.max', [
          String(MAX_DEFAULT_FIELD_SYMBOLS),
        ]) as string
      ),
    createdAt: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.datetime.required') as string
      ),
    updatedAt: yup
      .string()
      .trim()
      .required(
        localize('validation.admin.orders.datetime.required') as string
      ),
    shippingData: yup.object().shape({
      firstName: yup
        .string()
        .required(localize('validation.shipping.first.name.required') as string)
        .min(2, localize('validation.shipping.first.name.min') as string)
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(
            localize,
            'validation.shipping.first.name.max'
          )
        ),
      lastName: yup
        .string()
        .required(localize('validation.shipping.last.name.required') as string)
        .min(2, localize('validation.shipping.last.name.min') as string)
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(
            localize,
            'validation.shipping.last.name.max'
          )
        ),
      email: yup
        .string()
        .required(localize('validation.shipping.email.required') as string)
        .email(localize('validation.shipping.email.invalid') as string)
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(localize, 'validation.shipping.email.max')
        ),
      phone: yup
        .string()
        .required(localize('validation.shipping.phone.required') as string)
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(localize, 'validation.shipping.phone.max')
        )
        .matches(
          /^[0-9]{9}$/,
          localize('validation.shipping.phone.format') as string
        ),
      street: yup
        .string()
        .required(localize('validation.shipping.street.required') as string)
        .min(3, localize('validation.shipping.street.min') as string)
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(localize, 'validation.shipping.street.max')
        ),
      buildingNumber: yup
        .string()
        .required(
          localize('validation.shipping.building.number.required') as string
        )
        .matches(
          /^[0-9]+[A-Za-z]?$/,
          localize('validation.shipping.building.number.format') as string
        )
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(
            localize,
            'validation.shipping.building.number.max'
          )
        ),
      apartamentNumber: yup
        .string()
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(
            localize,
            'validation.shipping.apartment.number.max'
          )
        ),
      city: yup
        .string()
        .required(localize('validation.shipping.city.required') as string)
        .min(2, localize('validation.shipping.city.min') as string)
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(localize, 'validation.shipping.city.max')
        ),
      postalCode: yup
        .string()
        .required(
          localize('validation.shipping.postal.code.required') as string
        )
        .max(
          MAX_DEFAULT_FIELD_SYMBOLS,
          shippingDefaultMaxMessage(
            localize,
            'validation.shipping.postal.code.max'
          )
        )
        .matches(
          /^[0-9]{2}-[0-9]{3}$/,
          localize('validation.shipping.postal.code.format') as string
        ),
      additionalInfo: yup
        .string()
        .max(
          MAX_DEFAULT_BIG_FIELD_SYMBOLS,
          localize('validation.shipping.additional.info.max', [
            String(MAX_DEFAULT_BIG_FIELD_SYMBOLS),
          ]) as string
        ),
      deliveryMethod: yup
        .string()
        .required(
          localize('validation.shipping.delivery.method.required') as string
        )
        .oneOf(
          ['inpost'],
          localize('validation.shipping.delivery.method.oneof') as string
        ),
      parcelLockerCode: yup
        .string()
        .transform((v) => (typeof v === 'string' ? v.trim() : v))
        .when('deliveryMethod', {
          is: 'inpost',
          then: (schema) =>
            schema
              .required(
                localize(
                  'validation.shipping.parcel.locker.code.required'
                ) as string
              )
              .min(
                4,
                localize('validation.shipping.parcel.locker.code.min') as string
              )
              .max(
                MAX_DEFAULT_FIELD_SYMBOLS,
                shippingDefaultMaxMessage(
                  localize,
                  'validation.shipping.parcel.locker.code.max'
                )
              ),
          otherwise: (schema) => schema.optional(),
        }),
    }),
    checkoutItems: yup
      .array()
      .of(checkoutItemSchema)
      .min(1, localize('validation.admin.orders.checkout.min') as string)
      .required(),
  }) as yup.ObjectSchema<TOrderFormValues>;
};
